import { useState, useRef, useEffect } from 'react'
import { RotateCw, ZoomIn, ZoomOut, Check, X as XIcon } from 'lucide-react'

interface ImageEditorProps {
  image: File
  onSave: (editedFile: File) => void
  onCancel: () => void
  aspectRatio?: number
}

type DragType = 'image' | 'crop' | 'resize' | null
type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se' | null

const ImageEditor = ({ image, onSave, onCancel, aspectRatio = 1 }: ImageEditorProps) => {
  const [preview, setPreview] = useState<string>('')
  const [scale, setScale] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 0, height: 0 })
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  
  const [dragType, setDragType] = useState<DragType>(null)
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle>(null)
  const dragStartRef = useRef({ x: 0, y: 0 })
  const cropStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 })
  const positionStartRef = useRef({ x: 0, y: 0 })

  useEffect(() => {
    const reader = new FileReader()
    reader.onloadend = () => {
      setPreview(reader.result as string)
    }
    reader.readAsDataURL(image)
  }, [image])

  useEffect(() => {
    if (preview && imageRef.current && containerRef.current) {
      const img = imageRef.current
      const container = containerRef.current
      
      const updateLayout = () => {
        if (img.complete && container) {
          // Esperar múltiples frames para asegurar que el contenedor tenga sus dimensiones
          const checkDimensions = () => {
            const containerWidth = container.clientWidth
            const containerHeight = container.clientHeight
            
            if (containerWidth === 0 || containerHeight === 0) {
              // Si aún no tiene dimensiones, intentar de nuevo
              setTimeout(checkDimensions, 50)
              return
            }
            
            const imgWidth = img.naturalWidth
            const imgHeight = img.naturalHeight
            
            if (imgWidth === 0 || imgHeight === 0) return
            
            // Calcular escala inicial para que la imagen quepa en el contenedor
            const scaleX = (containerWidth * 0.95) / imgWidth
            const scaleY = (containerHeight * 0.95) / imgHeight
            const initialScale = Math.min(scaleX, scaleY, 1.2)
            
            setScale(initialScale)
            
            // Centrar la imagen (usando translate, así que calculamos desde 0,0)
            const scaledWidth = imgWidth * initialScale
            const scaledHeight = imgHeight * initialScale
            const newPosition = {
              x: (containerWidth - scaledWidth) / 2,
              y: (containerHeight - scaledHeight) / 2
            }
            setPosition(newPosition)
            
            // Inicializar área de recorte cuadrada centrada
            const cropSize = Math.min(scaledWidth, scaledHeight, containerWidth * 0.75, containerHeight * 0.75)
            setCropArea({
              x: (containerWidth - cropSize) / 2,
              y: (containerHeight - cropSize) / 2,
              width: cropSize,
              height: cropSize
            })
          }
          
          requestAnimationFrame(() => {
            requestAnimationFrame(checkDimensions)
          })
        }
      }
      
      if (img.complete) {
        updateLayout()
      } else {
        img.onload = updateLayout
      }
    }
  }, [preview, aspectRatio])

  // Manejar eventos de mouse y touch
  const getEventCoordinates = (e: MouseEvent | TouchEvent) => {
    if ('touches' in e) {
      const touch = e.touches[0] || e.changedTouches[0]
      const rect = containerRef.current?.getBoundingClientRect()
      return {
        x: touch.clientX - (rect?.left || 0),
        y: touch.clientY - (rect?.top || 0)
      }
    } else {
      const rect = containerRef.current?.getBoundingClientRect()
      return {
        x: e.clientX - (rect?.left || 0),
        y: e.clientY - (rect?.top || 0)
      }
    }
  }

  const handleImageStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const nativeEvent = e.nativeEvent as MouseEvent | TouchEvent
    const coords = getEventCoordinates(nativeEvent)
    setDragType('image')
    positionStartRef.current = position
    dragStartRef.current = coords
  }

  const handleCropStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const nativeEvent = e.nativeEvent as MouseEvent | TouchEvent
    const coords = getEventCoordinates(nativeEvent)
    setDragType('crop')
    cropStartRef.current = cropArea
    dragStartRef.current = coords
  }

  const handleResizeStart = (handle: ResizeHandle) => (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const nativeEvent = e.nativeEvent as MouseEvent | TouchEvent
    const coords = getEventCoordinates(nativeEvent)
    setDragType('resize')
    setResizeHandle(handle)
    cropStartRef.current = cropArea
    dragStartRef.current = coords
  }

  useEffect(() => {
    if (!dragType) return

    const handleMove = (e: MouseEvent | TouchEvent) => {
      e.preventDefault()
      const coords = getEventCoordinates(e)
      const deltaX = coords.x - dragStartRef.current.x
      const deltaY = coords.y - dragStartRef.current.y

      if (dragType === 'image') {
        const container = containerRef.current
        if (!container) return
        
        const img = imageRef.current
        if (!img) return
        
        const currentScale = scale
        const scaledWidth = img.naturalWidth * currentScale
        const scaledHeight = img.naturalHeight * currentScale
        
        const newX = positionStartRef.current.x + deltaX
        const newY = positionStartRef.current.y + deltaY
        
        // Limitar movimiento para mantener la imagen visible
        const minX = -(scaledWidth - container.clientWidth)
        const maxX = 0
        const minY = -(scaledHeight - container.clientHeight)
        const maxY = 0
        
        setPosition({
          x: Math.max(minX, Math.min(maxX, newX)),
          y: Math.max(minY, Math.min(maxY, newY))
        })
      } else if (dragType === 'crop') {
        const container = containerRef.current
        if (!container) return
        
        const newX = cropStartRef.current.x + deltaX
        const newY = cropStartRef.current.y + deltaY
        
        setCropArea(prev => ({
          ...prev,
          x: Math.max(0, Math.min(container.clientWidth - prev.width, newX)),
          y: Math.max(0, Math.min(container.clientHeight - prev.height, newY))
        }))
      } else if (dragType === 'resize' && resizeHandle) {
        const container = containerRef.current
        if (!container) return
        
        let newCropArea = { ...cropStartRef.current }
        
        if (resizeHandle === 'se') {
          newCropArea.width = Math.max(100, Math.min(
            container.clientWidth - cropStartRef.current.x,
            cropStartRef.current.width + deltaX
          ))
          newCropArea.height = aspectRatio ? newCropArea.width : Math.max(100, Math.min(
            container.clientHeight - cropStartRef.current.y,
            cropStartRef.current.height + deltaY
          ))
        } else if (resizeHandle === 'sw') {
          const newWidth = Math.max(100, cropStartRef.current.width - deltaX)
          const newHeight = aspectRatio ? newWidth : Math.max(100, cropStartRef.current.height + deltaY)
          newCropArea.x = Math.max(0, cropStartRef.current.x + cropStartRef.current.width - newWidth)
          newCropArea.y = cropStartRef.current.y
          newCropArea.width = newWidth
          newCropArea.height = newHeight
        } else if (resizeHandle === 'ne') {
          const newWidth = Math.max(100, cropStartRef.current.width + deltaX)
          const newHeight = aspectRatio ? newWidth : Math.max(100, cropStartRef.current.height - deltaY)
          newCropArea.x = cropStartRef.current.x
          newCropArea.y = Math.max(0, cropStartRef.current.y + cropStartRef.current.height - newHeight)
          newCropArea.width = newWidth
          newCropArea.height = newHeight
        } else if (resizeHandle === 'nw') {
          const newWidth = Math.max(100, cropStartRef.current.width - deltaX)
          const newHeight = aspectRatio ? newWidth : Math.max(100, cropStartRef.current.height - deltaY)
          newCropArea.x = Math.max(0, cropStartRef.current.x + cropStartRef.current.width - newWidth)
          newCropArea.y = Math.max(0, cropStartRef.current.y + cropStartRef.current.height - newHeight)
          newCropArea.width = newWidth
          newCropArea.height = newHeight
        }
        
        if (newCropArea.x + newCropArea.width > container.clientWidth) {
          newCropArea.width = container.clientWidth - newCropArea.x
          if (aspectRatio) newCropArea.height = newCropArea.width
        }
        if (newCropArea.y + newCropArea.height > container.clientHeight) {
          newCropArea.height = container.clientHeight - newCropArea.y
          if (aspectRatio) newCropArea.width = newCropArea.height
        }
        
        setCropArea(newCropArea)
      }
    }

    const handleEnd = () => {
      setDragType(null)
      setResizeHandle(null)
    }

    document.addEventListener('mousemove', handleMove)
    document.addEventListener('mouseup', handleEnd)
    document.addEventListener('touchmove', handleMove, { passive: false })
    document.addEventListener('touchend', handleEnd)

    return () => {
      document.removeEventListener('mousemove', handleMove)
      document.removeEventListener('mouseup', handleEnd)
      document.removeEventListener('touchmove', handleMove)
      document.removeEventListener('touchend', handleEnd)
    }
  }, [dragType, resizeHandle, scale, aspectRatio])

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev * 1.2, 3))
  }

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev * 0.8, 0.5))
  }

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360)
  }

  const handleSave = async () => {
    if (!canvasRef.current || !imageRef.current) return

    const canvas = canvasRef.current
    const img = imageRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const outputSize = 400
    canvas.width = outputSize
    canvas.height = outputSize
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Calcular las coordenadas del área de recorte en relación a la imagen original
    // cropArea está en coordenadas del contenedor
    // position es el translate de la imagen
    // Necesitamos convertir las coordenadas del contenedor a coordenadas de la imagen original
    
    // La esquina superior izquierda del área de recorte en coordenadas del contenedor
    const cropX = cropArea.x
    const cropY = cropArea.y
    
    // Convertir a coordenadas relativas a la imagen (después del translate)
    // Si la imagen está en position.x, position.y, entonces:
    // - cropX - position.x nos da la posición relativa a la imagen escalada
    // - Dividimos por scale para obtener coordenadas de la imagen original
    const relativeX = (cropX - position.x) / scale
    const relativeY = (cropY - position.y) / scale
    const sourceWidth = cropArea.width / scale
    const sourceHeight = cropArea.height / scale

    // Asegurar que las coordenadas estén dentro de los límites de la imagen
    const sourceX = Math.max(0, Math.min(relativeX, img.naturalWidth - sourceWidth))
    const sourceY = Math.max(0, Math.min(relativeY, img.naturalHeight - sourceHeight))
    
    // Ajustar el ancho y alto si es necesario
    const finalSourceWidth = Math.min(sourceWidth, img.naturalWidth - sourceX)
    const finalSourceHeight = Math.min(sourceHeight, img.naturalHeight - sourceY)

    if (rotation !== 0) {
      // Si hay rotación, primero rotamos la imagen completa
      const tempCanvas = document.createElement('canvas')
      const tempCtx = tempCanvas.getContext('2d')
      if (!tempCtx) return

      const angle = (rotation * Math.PI) / 180
      const cos = Math.abs(Math.cos(angle))
      const sin = Math.abs(Math.sin(angle))
      const rotatedWidth = img.naturalWidth * cos + img.naturalHeight * sin
      const rotatedHeight = img.naturalWidth * sin + img.naturalHeight * cos

      tempCanvas.width = rotatedWidth
      tempCanvas.height = rotatedHeight

      // Rotar la imagen completa
      tempCtx.save()
      tempCtx.translate(rotatedWidth / 2, rotatedHeight / 2)
      tempCtx.rotate(angle)
      tempCtx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2)
      tempCtx.restore()

      // Calcular las coordenadas del área de recorte en la imagen rotada
      // El área de recorte original estaba en sourceX, sourceY de la imagen no rotada
      // Necesitamos encontrar dónde está esa área en la imagen rotada
      const centerX = img.naturalWidth / 2
      const centerY = img.naturalHeight / 2
      
      // Las esquinas del área de recorte en la imagen original
      const topLeft = { x: sourceX, y: sourceY }
      const topRight = { x: sourceX + finalSourceWidth, y: sourceY }
      const bottomLeft = { x: sourceX, y: sourceY + finalSourceHeight }
      const bottomRight = { x: sourceX + finalSourceWidth, y: sourceY + finalSourceHeight }
      
      // Rotar estas coordenadas
      const rotatePoint = (point: { x: number, y: number }) => {
        const dx = point.x - centerX
        const dy = point.y - centerY
        const rotatedX = dx * Math.cos(angle) - dy * Math.sin(angle) + rotatedWidth / 2
        const rotatedY = dx * Math.sin(angle) + dy * Math.cos(angle) + rotatedHeight / 2
        return { x: rotatedX, y: rotatedY }
      }
      
      const rotatedTopLeft = rotatePoint(topLeft)
      const rotatedTopRight = rotatePoint(topRight)
      const rotatedBottomLeft = rotatePoint(bottomLeft)
      const rotatedBottomRight = rotatePoint(bottomRight)
      
      // Encontrar el bounding box del área rotada
      const minX = Math.min(rotatedTopLeft.x, rotatedTopRight.x, rotatedBottomLeft.x, rotatedBottomRight.x)
      const minY = Math.min(rotatedTopLeft.y, rotatedTopRight.y, rotatedBottomLeft.y, rotatedBottomRight.y)
      const maxX = Math.max(rotatedTopLeft.x, rotatedTopRight.x, rotatedBottomLeft.x, rotatedBottomRight.x)
      const maxY = Math.max(rotatedTopLeft.y, rotatedTopRight.y, rotatedBottomLeft.y, rotatedBottomRight.y)
      
      const rotatedSourceX = Math.max(0, minX)
      const rotatedSourceY = Math.max(0, minY)
      const rotatedSourceWidth = Math.min(maxX - minX, rotatedWidth - rotatedSourceX)
      const rotatedSourceHeight = Math.min(maxY - minY, rotatedHeight - rotatedSourceY)

      ctx.drawImage(
        tempCanvas,
        rotatedSourceX,
        rotatedSourceY,
        rotatedSourceWidth,
        rotatedSourceHeight,
        0,
        0,
        outputSize,
        outputSize
      )
    } else {
      // Sin rotación, dibujar directamente el área recortada
      ctx.drawImage(
        img,
        sourceX,
        sourceY,
        finalSourceWidth,
        finalSourceHeight,
        0,
        0,
        outputSize,
        outputSize
      )
    }

    canvas.toBlob((blob) => {
      if (blob) {
        const editedFile = new File([blob], image.name, {
          type: image.type,
          lastModified: Date.now()
        })
        onSave(editedFile)
      }
    }, image.type, 0.9)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
      <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" style={{
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.05)'
      }}>
        {/* Header estilo iPhone */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 bg-gradient-to-b from-white to-gray-50/50">
          <h2 className="text-2xl font-semibold text-gray-900 tracking-tight">Editar Foto</h2>
          <button
            onClick={onCancel}
            className="p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-full transition-all duration-150"
          >
            <XIcon className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Editor */}
        <div className="flex-1 overflow-auto p-6 bg-gradient-to-br from-gray-50 to-white">
          <div
            ref={containerRef}
            className="relative w-full h-96 sm:h-[500px] bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl overflow-hidden border border-gray-200/50 shadow-inner"
            style={{
              boxShadow: 'inset 0 2px 8px rgba(0, 0, 0, 0.1)'
            }}
          >
            {preview && (
              <>
                {/* Imagen */}
                <img
                  ref={imageRef}
                  src={preview}
                  alt="Preview"
                  className="absolute select-none pointer-events-auto"
                  style={{
                    transform: `translate(${position.x}px, ${position.y}px) scale(${scale}) rotate(${rotation}deg)`,
                    transformOrigin: '0 0',
                    cursor: dragType === 'image' ? 'grabbing' : 'grab',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    touchAction: 'none',
                    willChange: 'transform'
                  }}
                  onMouseDown={handleImageStart}
                  onTouchStart={handleImageStart}
                  draggable={false}
                />

                {/* Área de recorte */}
                {cropArea.width > 0 && (
                  <>
                    <div
                      className="absolute border-3 border-white bg-transparent z-10 shadow-2xl"
                      style={{
                        left: `${cropArea.x}px`,
                        top: `${cropArea.y}px`,
                        width: `${cropArea.width}px`,
                        height: `${cropArea.height}px`,
                        cursor: dragType === 'crop' ? 'grabbing' : 'move',
                        borderWidth: '3px',
                        boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.1), 0 0 20px rgba(0, 0, 0, 0.2)'
                      }}
                      onMouseDown={handleCropStart}
                      onTouchStart={handleCropStart}
                    >
                      {/* Controles de esquina para redimensionar */}
                      <div
                        className="absolute -top-3 -left-3 w-7 h-7 bg-white rounded-full border-2 border-primary-500 shadow-lg cursor-nwse-resize touch-none active:scale-110 transition-transform"
                        style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)' }}
                        onMouseDown={handleResizeStart('nw')}
                        onTouchStart={handleResizeStart('nw')}
                      />
                      <div
                        className="absolute -top-3 -right-3 w-7 h-7 bg-white rounded-full border-2 border-primary-500 shadow-lg cursor-nesw-resize touch-none active:scale-110 transition-transform"
                        style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)' }}
                        onMouseDown={handleResizeStart('ne')}
                        onTouchStart={handleResizeStart('ne')}
                      />
                      <div
                        className="absolute -bottom-3 -left-3 w-7 h-7 bg-white rounded-full border-2 border-primary-500 shadow-lg cursor-nesw-resize touch-none active:scale-110 transition-transform"
                        style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)' }}
                        onMouseDown={handleResizeStart('sw')}
                        onTouchStart={handleResizeStart('sw')}
                      />
                      <div
                        className="absolute -bottom-3 -right-3 w-7 h-7 bg-white rounded-full border-2 border-primary-500 shadow-lg cursor-nwse-resize touch-none active:scale-110 transition-transform"
                        style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)' }}
                        onMouseDown={handleResizeStart('se')}
                        onTouchStart={handleResizeStart('se')}
                      />
                    </div>

                    {/* Overlay oscuro fuera del área de recorte */}
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background: `rgba(0, 0, 0, 0.6)`,
                        clipPath: `polygon(
                          0% 0%, 0% 100%,
                          ${cropArea.x}px 100%,
                          ${cropArea.x}px ${cropArea.y}px,
                          ${cropArea.x + cropArea.width}px ${cropArea.y}px,
                          ${cropArea.x + cropArea.width}px ${cropArea.y + cropArea.height}px,
                          ${cropArea.x}px ${cropArea.y + cropArea.height}px,
                          ${cropArea.x}px 100%,
                          100% 100%, 100% 0%
                        )`
                      }}
                    />
                  </>
                )}
              </>
            )}
          </div>

          {/* Canvas oculto para procesamiento */}
          <canvas ref={canvasRef} className="hidden" />
        </div>

        {/* Controles estilo iPhone */}
        <div className="p-6 border-t border-gray-100 bg-white">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-4">
            {/* Botones de edición */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleZoomOut}
                className="p-3.5 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 border border-gray-200 rounded-2xl transition-all duration-150 active:scale-95 shadow-sm"
                title="Alejar"
              >
                <ZoomOut className="w-5 h-5 text-gray-700" />
              </button>
              <button
                onClick={handleZoomIn}
                className="p-3.5 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 border border-gray-200 rounded-2xl transition-all duration-150 active:scale-95 shadow-sm"
                title="Acercar"
              >
                <ZoomIn className="w-5 h-5 text-gray-700" />
              </button>
              <button
                onClick={handleRotate}
                className="p-3.5 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 border border-gray-200 rounded-2xl transition-all duration-150 active:scale-95 shadow-sm"
                title="Rotar 90°"
              >
                <RotateCw className="w-5 h-5 text-gray-700" />
              </button>
            </div>

            {/* Botones de acción */}
            <div className="flex items-center gap-3">
              <button
                onClick={onCancel}
                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-700 rounded-2xl transition-all duration-150 font-semibold active:scale-95 shadow-sm"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white rounded-2xl transition-all duration-150 font-semibold flex items-center gap-2 active:scale-95 shadow-lg shadow-primary-500/30"
              >
                <Check className="w-5 h-5" />
                Guardar
              </button>
            </div>
          </div>

          {/* Instrucciones */}
          <div className="text-center space-y-1 pt-2 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500">
              💡 Arrastra la imagen para moverla • Arrastra el marco para reposicionarlo • Arrastra las esquinas para redimensionar
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImageEditor
